import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  meetingRecords as meetingsApi,
  meetingMinutes as minutesApi,
  meetingActions as actionsApi
} from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, todayISO } from '../../utils/format';
import { printableRows, printReference } from '../../utils/printRows';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, TimeField, PrintButton
} from '../../components/ui';
import MeetingSummary from './MeetingSummary';

const MEETING_STATUSES = ['Scheduled', 'Completed', 'Cancelled'];
const MINUTE_STATUSES = [
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Deferred', label: 'Deferred / Postponed' }
];
const ACTION_STATUSES = ['Pending', 'In Progress', 'Completed'];

const EMPTY_MEETING = { title: '', type: '', committee: '', date: '', time: '', venue: '', status: 'Scheduled', remarks: '' };
const EMPTY_MINUTE = { meetingRef: '', agendaTopic: '', discussion: '', decision: '', resolutionNo: '', budget: '', status: 'Approved' };
const EMPTY_ACTION = { resolutionRef: '', task: '', assignedTo: '', dueDate: '', status: 'Pending', remarks: '' };

// Cross-links store the target's recordId but are picked from a readable
// label — "MTG-1004 - Q3 Finance Planning (12/07/2026)".
const refId = (label) => String(label).split(' - ')[0].trim();

/** The three sibling resources behind this screen. */
type MeetingTab = 'meetings' | 'minutes' | 'actions';
/** The Summary tab reads all three; the rest of the screen works on one. */
type MeetingView = MeetingTab | 'summary';

const MeetingManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<MeetingView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: MeetingTab = isSummary ? 'meetings' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [mtgForm, setMtgForm] = useState(EMPTY_MEETING);
  const [minForm, setMinForm] = useState(EMPTY_MINUTE);
  const [actForm, setActForm] = useState(EMPTY_ACTION);
  const [editingId, setEditingId] = useState(null);

  const isEditing = editingId !== null;
  const isDeletedView = viewMode === 'deleted';
  const { notification, notify, dismiss } = useNotification();

  const ledger = useMultiLedger({
    meetings: { resource: meetingsApi, label: 'Meeting', prefix: 'MTG', statusField: 'meetingStatus' },
    minutes: { resource: minutesApi, label: 'Minutes/Resolution', prefix: 'MIN', statusField: 'minuteStatus' },
    actions: { resource: actionsApi, label: 'Action Item', prefix: 'ACT', statusField: 'actionStatus' }
  }, { notify });

  const mtgTypes = useOptionList('church_mtg_types');
  const committees = useOptionList('church_mtg_comm');
  const assignees = useOptionList('church_mtg_assignees');

  const meetings = ledger.active('meetings');
  const minutes = ledger.active('minutes');

  // Lookups so minutes can show their meeting, and actions their resolution.
  const meetingById = useMemo(
    () => meetings.reduce((acc, m) => ({ ...acc, [m.recordId]: m }), {}),
    [meetings]
  );
  const minuteById = useMemo(
    () => minutes.reduce((acc, m) => ({ ...acc, [m.recordId]: m }), {}),
    [minutes]
  );

  const meetingLabels = useMemo(
    () => meetings.map((m) => `${m.recordId} - ${m.title} (${formatDate(m.date)})`),
    [meetings]
  );
  const resolutionLabels = useMemo(
    () => minutes.map((m) => `${m.recordId} - ${m.agendaTopic}${m.resolutionNo ? ` [${m.resolutionNo}]` : ''}`),
    [minutes]
  );

  /**
   * Voiding a parent voids everything hanging off it: a Meeting takes its
   * Minutes (and each of those Minutes' Action Items) with it, and voiding a
   * single Minutes record takes its own Action Items. Without this the children
   * stay active while pointing at a record that no longer exists.
   *
   * Runs as the confirmVoid hook, before the refetch. Each resource keeps its
   * live/void flag in its own column as well as `status`, so both are stamped.
   */
  const cascadeVoidChildren = useCallback(async (voidedRow) => {
    if (voidedRow.__key === 'actions') return;

    const reason = `Cascade void from ${voidedRow.recordId}`;
    const deletedAt = voidedRow.deletedAt || new Date().toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

    const childMinutes = voidedRow.__key === 'meetings'
      ? ledger.active('minutes').filter((m) => m.mtgId === voidedRow.recordId)
      : [];
    // Actions orphan twice over: directly under a voided Minutes record, or
    // indirectly under the Minutes of a voided Meeting.
    const parentMinuteIds = voidedRow.__key === 'meetings'
      ? childMinutes.map((m) => m.recordId)
      : [voidedRow.recordId];
    const childActions = ledger.active('actions')
      .filter((a) => parentMinuteIds.includes(a.minId));

    if (childMinutes.length === 0 && childActions.length === 0) return;

    const results = await Promise.allSettled([
      ...childMinutes.map((m) => minutesApi.update(m.rawId, {
        ...m, id: m.rawId, status: 'VOID', minuteStatus: 'VOID', deleteReason: reason, deletedAt
      })),
      ...childActions.map((a) => actionsApi.update(a.rawId, {
        ...a, id: a.rawId, status: 'VOID', actionStatus: 'VOID', deleteReason: reason, deletedAt
      }))
    ]);

    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      notify(`${failed} of ${results.length} linked records could not be voided.`, 'error');
      return;
    }

    const parts = [
      childMinutes.length && `${childMinutes.length} minute(s)`,
      childActions.length && `${childActions.length} action item(s)`
    ].filter(Boolean).join(' and ');
    notify(`${parts} voided along with ${voidedRow.recordId}.`, 'success');
  }, [ledger, notify]);

  const dataset = isDeletedView ? ledger.voided(activeTab) : ledger.active(activeTab);

  const getRowDate = useCallback((r) => r.date || r.dueDate || r.dateRecorded, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'title', 'type', 'committee', 'agendaTopic', 'resolutionNo', 'task', 'assignedTo']
  );
  const page = usePagination(filtered);

  const resetForms = () => {
    setMtgForm(EMPTY_MEETING);
    setMinForm(EMPTY_MINUTE);
    setActForm(EMPTY_ACTION);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  const startEdit = (item) => {
    if (activeTab === 'meetings') {
      setMtgForm({
        title: item.title || '', type: item.type || '', committee: item.committee || '',
        date: item.date || '', time: item.time || '', venue: item.venue || '',
        status: item.meetingStatus || 'Scheduled', remarks: item.remarks || ''
      });
    } else if (activeTab === 'minutes') {
      const linked = meetingById[item.mtgId];
      setMinForm({
        meetingRef: linked ? `${linked.recordId} - ${linked.title}` : item.mtgId || '',
        agendaTopic: item.agendaTopic || '', discussion: item.discussion || '',
        decision: item.decision || '', resolutionNo: item.resolutionNo || '',
        budget: item.budget || '', status: item.minuteStatus || 'Approved'
      });
    } else {
      const linked = minuteById[item.minId];
      setActForm({
        resolutionRef: linked ? `${linked.recordId} - ${linked.agendaTopic}` : item.minId || '',
        task: item.task || '', assignedTo: item.assignedTo || '',
        dueDate: item.dueDate || '', status: item.actionStatus || 'Pending', remarks: item.remarks || ''
      });
    }
    setEditingId(item.rawId);
    setViewMode('add');
  };

  // "Open the record" from the Approvals inbox. These records have no view of
  // their own, so a live one opens in its edit form; a deleted one is shown,
  // found by its number, in its tab's Deleted list.
  const [linkRow, setLinkRow] = useState(null);
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref) as (typeof ledger.rows)[number] & { recordId?: string } | null;
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as MeetingTab;
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) {
      setViewMode('deleted');
      setSearchTerm(row.recordId || '');
    } else {
      setLinkRow(row);
    }
  }, !ledger.loading);
  // startEdit reads the tab it is on: wait until the switch has rendered.
  useEffect(() => {
    if (!linkRow || activeTab !== linkRow.__key) return;
    startEdit(linkRow);
    setLinkRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkRow, activeTab]);

  const existingRecordId = (key) =>
    isEditing
      ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string } | undefined)?.recordId
      : null;

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    const type = mtgForm.type.trim();
    const committee = mtgForm.committee.trim();
    if (!type || !committee) { notify('Meeting Type and Committee are required.', 'error'); return; }

    mtgTypes.ensure(type);
    committees.ensure(committee);

    const payload = {
      ...mtgForm,
      type,
      committee,
      meetingStatus: mtgForm.status,
      recordId: existingRecordId('meetings') || ledger.nextSequence('meetings'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('meetings', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleMinuteSubmit = async (e) => {
    e.preventDefault();
    const mtgId = refId(minForm.meetingRef);
    if (!mtgId) { notify('Meeting selection is required.', 'error'); return; }

    const existing = isEditing ? ledger.active('minutes').find((m) => m.rawId === editingId) : null;
    const { meetingRef, ...rest } = minForm;
    const payload = {
      ...rest,
      mtgId,
      budget: minForm.budget ? parseFloat(minForm.budget) : 0.0,
      minuteStatus: minForm.status,
      dateRecorded: existing?.dateRecorded || todayISO(),
      recordId: existing?.recordId || ledger.nextSequence('minutes'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('minutes', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    const minId = refId(actForm.resolutionRef);
    const assignedTo = actForm.assignedTo.trim();
    const missing = missingRequired([[minId, 'Resolution/Minutes'], [assignedTo, 'Assignee']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    assignees.ensure(assignedTo);

    const { resolutionRef, ...rest } = actForm;
    const payload = {
      ...rest,
      minId,
      assignedTo,
      actionStatus: actForm.status,
      recordId: existingRecordId('actions') || ledger.nextSequence('actions'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('actions', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const sub = { fontSize: '12px', color: t.textFaint };
  const idColumn = { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> };

  const columnsByTab = {
    meetings: [
      idColumn,
      {
        key: 'title',
        header: 'Meeting Title & Date',
        render: (r) => (<><strong>{r.title}</strong><br /><span style={sub}>{formatDate(r.date)} at {r.time}</span></>)
      },
      {
        key: 'type',
        header: 'Type & Committee',
        render: (r) => (<><strong>{r.type}</strong><br /><span style={sub}>{r.committee}</span></>)
      },
      {
        key: 'venue',
        header: 'Venue & Remarks',
        wrap: true,
        // Remarks sit under the venue the way an action item's do under its
        // task — the meetings tab had no column showing them at all.
        render: (r) => (
          <>
            {r.venue || '-'}
            {r.remarks && <><br /><span style={sub}>{r.remarks}</span></>}
          </>
        )
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => (
          <Badge tone={r.meetingStatus === 'Completed' ? 'success' : r.meetingStatus === 'Cancelled' ? 'error' : 'neutral'}>
            {r.meetingStatus || '-'}
          </Badge>
        )
      }
    ],
    minutes: [
      idColumn,
      {
        key: 'meeting',
        header: 'Meeting Info',
        render: (r) => {
          const m = meetingById[r.mtgId];
          return (
            <>
              <strong>{m?.title || r.mtgId || 'Unknown Meeting'}</strong>
              {m && <><br /><span style={sub}>{formatDate(m.date)}</span></>}
            </>
          );
        }
      },
      { key: 'agendaTopic', header: 'Agenda / Topic', render: (r) => <strong>{r.agendaTopic}</strong> },
      {
        key: 'decision',
        header: 'Decision & Res. No',
        render: (r) => (
          <>
            {r.decision}
            {r.resolutionNo && (
              <><br /><span style={{ fontSize: '11px', fontWeight: 'bold', color: t.primaryText }}>[Res: {r.resolutionNo}]</span></>
            )}
          </>
        )
      },
      {
        key: 'budget',
        header: 'Budget',
        align: 'right',
        nowrap: true,
        render: (r) => <span style={{ fontWeight: 'bold', color: t.income }}>{formatCurrency(r.budget)}</span>
      }
    ],
    actions: [
      idColumn,
      {
        key: 'resolution',
        header: 'Resolution Link',
        render: (r) => (
          <span style={{ fontSize: '12px', color: t.textMuted }}>
            {minuteById[r.minId]?.agendaTopic || r.minId || 'Unknown Resolution'}
          </span>
        )
      },
      {
        key: 'task',
        header: 'Task Description',
        render: (r) => (
          <>
            <strong>{r.task}</strong>
            {r.remarks && <><br /><span style={{ fontSize: '11px', color: t.textFaint }}>{r.remarks}</span></>}
          </>
        )
      },
      { key: 'assignedTo', header: 'Assigned To', render: (r) => <strong>{r.assignedTo}</strong> },
      {
        key: 'dueDate',
        header: 'Due Date & Status',
        nowrap: true,
        render: (r) => (
          <>
            {formatDate(r.dueDate)}<br />
            <Badge tone={r.actionStatus === 'Completed' ? 'success' : r.actionStatus === 'Pending' ? 'warning' : 'neutral'}>
              {r.actionStatus || '-'}
            </Badge>
          </>
        )
      }
    ]
  };

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          render: (r) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
              <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
            </div>
          )
        }
      ];

  // Row serial, continuous across pages rather than restarting at 1 on each —
  // same pattern as Committee History and Pastor Turnover.
  const serialColumn = {
    key: 'serial',
    header: 'S.No',
    nowrap: true,
    render: (r, idx) => <strong>{(page.currentPage - 1) * page.itemsPerPage + idx + 1}</strong>
  };

  const columns = [serialColumn, ...columnsByTab[activeTab], { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }, ...tailColumns];

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🤝"
        title="Meeting Management"
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'meetings', label: '📅 Meetings' },
          { key: 'minutes', label: '📝 Minutes & Resolutions' },
          { key: 'actions', label: '✅ Action Items' }
        ]}
      />

      {isSummary && (
        <MeetingSummary
          meetings={meetings}
          minutes={minutes}
          actions={ledger.active('actions')}
          loading={ledger.loading}
          isDarkMode={isDarkMode}
        />
      )}

      {!isSummary && (
      <SubTabs
        value={viewMode}
        onChange={switchView}
        tabs={[
          { key: 'history', label: '📋 View Active Records' },
          { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
          { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
        ]}
      />
      )}

      {!isSummary && (viewMode === 'history' || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Title, Committee, Topic, Task or ID..."
            actions={
              <>
                <PrintButton
                  module="Meeting Management"
                  documentType={(({ meetings: { doc: 'MINUTES_EXTRACT', title: 'Meeting Register' }, minutes: { doc: 'MINUTES_REGISTER', title: 'Minutes Book Extract' }, actions: { doc: 'ACTION_ITEMS_REGISTER', title: 'Action Items Register' } })[activeTab]).doc}
                  size="sm"
                  title={(({ meetings: { doc: 'MINUTES_EXTRACT', title: 'Meeting Register' }, minutes: { doc: 'MINUTES_REGISTER', title: 'Minutes Book Extract' }, actions: { doc: 'ACTION_ITEMS_REGISTER', title: 'Action Items Register' } })[activeTab]).title.replace(/[^A-Za-z0-9]+/g, '-')}
                  // Each tab prints its own register, with every row that matches the
                  // filters — dates formatted and Created By on every line.
                  data={() => ({
                    title: `${(({ meetings: { doc: 'MINUTES_EXTRACT', title: 'Meeting Register' }, minutes: { doc: 'MINUTES_REGISTER', title: 'Minutes Book Extract' }, actions: { doc: 'ACTION_ITEMS_REGISTER', title: 'Action Items Register' } })[activeTab]).title}${isDeletedView ? ' (Deleted)' : ''}`,
                    reference: printReference(searchTerm && `Search: "${searchTerm}"`),
                    rowCount: filtered.length,
                    rows: printableRows(filtered)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                <DateRangeFilter {...range} onChange={page.reset} />
              </>
            }
          />

          <TablePanel
            title={<>Meeting Management — {activeTab === 'meetings' ? 'Meetings' : activeTab === 'minutes' ? 'Minutes & Resolutions' : 'Action Items'}</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              loading={ledger.loading}
              emptyMessage="No records found for this category."
          />
        </>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'meetings' && (
        <FormLayout
          title={isEditing ? 'Edit Meeting' : 'Schedule New Meeting'}
          submitLabel={isEditing ? 'Update Meeting' : 'Schedule Meeting'}
          submitting={ledger.saving}
          onSubmit={handleMeetingSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <Input
              label="Meeting Title / Subject" required fullWidth placeholder="e.g. Q3 Finance Planning"
              value={mtgForm.title}
              onChange={(e) => setMtgForm({ ...mtgForm, title: e.target.value })}
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            />
            <SearchableSelect
              label="Meeting Type" required placeholder="Search type..."
              value={mtgForm.type}
              onChange={(v) => setMtgForm((prev) => ({ ...prev, type: v }))}
              options={mtgTypes.options} onOptionsChange={mtgTypes.setOptions}
            />
            <SearchableSelect
              label="Responsible Committee" required placeholder="Search committee..."
              value={mtgForm.committee}
              onChange={(v) => setMtgForm((prev) => ({ ...prev, committee: v }))}
              options={committees.options} onOptionsChange={committees.setOptions}
            />
            <Input
              label="Date" type="date" required
              value={mtgForm.date}
              onChange={(e) => setMtgForm({ ...mtgForm, date: e.target.value })}
            />
            {/* Picked, not typed — a free-text box let "10 oclock" through
                and stored whatever was entered (CHM-120). */}
            <TimeField
              label="Time" required
              value={mtgForm.time}
              onChange={(v) => setMtgForm({ ...mtgForm, time: v })}
              hint="e.g. 10:00 AM"
            />
            <Input
              label="Venue" required placeholder="e.g. Parish Hall"
              value={mtgForm.venue}
              onChange={(e) => setMtgForm({ ...mtgForm, venue: e.target.value })}
            />
            <Select
              label="Status" placeholder=""
              options={MEETING_STATUSES}
              value={mtgForm.status}
              onChange={(e) => setMtgForm({ ...mtgForm, status: e.target.value })}
            />
            <Input
              label="Remarks / Pre-requisites" fullWidth
              placeholder="e.g. Please bring previous financial reports..."
              value={mtgForm.remarks}
              onChange={(e) => setMtgForm({ ...mtgForm, remarks: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'minutes' && (
        <FormLayout
          title={isEditing ? 'Edit Minutes' : 'Record Minutes & Resolution'}
          submitLabel={isEditing ? 'Update Minutes' : 'Save Minutes'}
          submitting={ledger.saving}
          onSubmit={handleMinuteSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Select Meeting" required fullWidth
              allowAdd={false} allowManage={false}
              placeholder="Search Scheduled/Completed Meetings..."
              value={minForm.meetingRef}
              onChange={(v) => setMinForm((prev) => ({ ...prev, meetingRef: v }))}
              options={meetingLabels}
            />
            <Input
              label="Agenda Topic" required fullWidth placeholder="e.g. Building Repair"
              value={minForm.agendaTopic}
              onChange={(e) => setMinForm({ ...minForm, agendaTopic: e.target.value })}
            />
            <Input
              label="Discussion Summary" type="textarea" rows={3} required fullWidth
              placeholder="Briefly describe what was discussed..."
              value={minForm.discussion}
              onChange={(e) => setMinForm({ ...minForm, discussion: e.target.value })}
            />
            <Input
              label="Final Decision / Resolution Passed" type="textarea" rows={2} required fullWidth
              placeholder="e.g. Approved the repair of the church roof..."
              value={minForm.decision}
              onChange={(e) => setMinForm({ ...minForm, decision: e.target.value })}
              style={{ border: `1px solid ${t.primary}` }}
            />
            <Input
              label="Resolution Number (If Formal)" placeholder="e.g. CC/2026/015"
              value={minForm.resolutionNo}
              onChange={(e) => setMinForm({ ...minForm, resolutionNo: e.target.value })}
            />
            <Input
              label="Approved Budget (₹) (If any)" type="number" min="0" step="0.01" placeholder="0.00"
              value={minForm.budget}
              onChange={(e) => setMinForm({ ...minForm, budget: e.target.value })}
              style={{ color: t.income, fontWeight: 'bold' }}
            />
            <Select
              label="Status" placeholder=""
              options={MINUTE_STATUSES}
              value={minForm.status}
              onChange={(e) => setMinForm({ ...minForm, status: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'actions' && (
        <FormLayout
          title={isEditing ? 'Edit Action Item' : 'Assign Action Item'}
          submitLabel={isEditing ? 'Update Action Item' : 'Assign Action'}
          submitting={ledger.saving}
          onSubmit={handleActionSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Link to Resolution / Agenda Topic" required fullWidth
              allowAdd={false} allowManage={false}
              placeholder="Search recorded minutes..."
              value={actForm.resolutionRef}
              onChange={(v) => setActForm((prev) => ({ ...prev, resolutionRef: v }))}
              options={resolutionLabels}
            />
            <Input
              label="Action / Task Description" required fullWidth
              placeholder="e.g. Obtain 3 quotations for roof repair..."
              value={actForm.task}
              onChange={(e) => setActForm({ ...actForm, task: e.target.value })}
            />
            <SearchableSelect
              label="Assigned To / Responsible" required placeholder="Search assignee..."
              value={actForm.assignedTo}
              onChange={(v) => setActForm((prev) => ({ ...prev, assignedTo: v }))}
              options={assignees.options} onOptionsChange={assignees.setOptions}
            />
            <Input
              label="Target Due Date" type="date" required
              value={actForm.dueDate}
              onChange={(e) => setActForm({ ...actForm, dueDate: e.target.value })}
            />
            <Select
              label="Progress Status" placeholder=""
              options={ACTION_STATUSES}
              value={actForm.status}
              onChange={(e) => setActForm({ ...actForm, status: e.target.value })}
            />
            <Input
              label="Remarks / Updates" fullWidth
              placeholder="e.g. Waiting for vendor response..."
              value={actForm.remarks}
              onChange={(e) => setActForm({ ...actForm, remarks: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason, cascadeVoidChildren)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default MeetingManagement;
